import {
  IMockFeatureToggleTable,
  IMockOrganizationTable,
  IMockUsersTable,
} from 'mocks/types/mockDbTables';
import { faker } from '@faker-js/faker';

// ~80% of toggles target Everyone (no org/user whitelist) and ~20% target
// specific orgs and/or users, to mirror prod where most toggles are global.
const TARGETED_AUDIENCE_RATE = 0.2;

const pickOrgIds = (currentOrgs: IMockOrganizationTable[]): string[] =>
  faker.helpers
    .arrayElements(currentOrgs, faker.helpers.rangeToNumber({ min: 1, max: 5 }))
    .map((x) => x.orgId);

const pickUserIds = (currentUsers: IMockUsersTable[]): string[] =>
  faker.helpers
    .arrayElements(
      currentUsers,
      faker.helpers.rangeToNumber({ min: 1, max: 5 })
    )
    .map((x) => x.userId);

const assignAudience = (
  currentUsers: IMockUsersTable[],
  currentOrgs: IMockOrganizationTable[]
): Pick<IMockFeatureToggleTable, 'selectedOrgs' | 'selectedUsers'> => {
  // Everyone — no whitelist (empty orgs + empty users => 'ALL'/'ALL').
  if (!faker.datatype.boolean({ probability: TARGETED_AUDIENCE_RATE })) {
    return { selectedOrgs: [], selectedUsers: [] };
  }

  // Targeted — orgs, users, or both. Guarantee at least one is non-empty.
  const includeOrgs = faker.datatype.boolean();
  const includeUsers = !includeOrgs || faker.datatype.boolean();
  return {
    selectedOrgs: includeOrgs ? pickOrgIds(currentOrgs) : [],
    selectedUsers: includeUsers ? pickUserIds(currentUsers) : [],
  };
};

export const generateUsers = (count: number): IMockUsersTable[] => {
  const allUsers: IMockUsersTable[] = [];

  Array.from(Array(count)).forEach(() => {
    allUsers.push({
      userId: faker.string.uuid(),
      fullName: faker.person.fullName(),
    });
  });

  return allUsers;
};

export const generateOrgs = (count: number): IMockOrganizationTable[] => {
  const allOrganizations: IMockOrganizationTable[] = [];

  Array.from(Array(count)).forEach(() => {
    allOrganizations.push({
      orgId: faker.string.uuid(),
      name: faker.company.name(),
    });
  });

  return allOrganizations;
};

const toPascalWord = (w: string): string =>
  w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();

// Prod toggles use PascalCase with an optional uppercase acronym prefix, e.g.
// "BANCommunicationPreferencesBackup". Build similar names from hacker nouns.
const generateToggleName = (): string => {
  const wordCount = faker.helpers.rangeToNumber({ min: 2, max: 4 });
  // Collect distinct PascalCase words so names don't repeat (e.g. "BusBus").
  const words = new Set<string>();
  let guard = 0;
  while (words.size < wordCount && guard < 20) {
    const word = faker.hacker
      .noun()
      .split(/[^a-zA-Z0-9]+/)
      .filter(Boolean)
      .map(toPascalWord)
      .join('');
    if (word) words.add(word);
    guard += 1;
  }
  const prefix = faker.datatype.boolean({ probability: 0.4 })
    ? faker.hacker.abbreviation()
    : '';
  return `${prefix}${Array.from(words).join('')}`;
};

export const generateFeatureToggles = (
  count: number,
  currentUsers: IMockUsersTable[],
  currentOrgs: IMockOrganizationTable[]
): IMockFeatureToggleTable[] => {
  const allFeatureToggles: IMockFeatureToggleTable[] = [];
  const usedNames = new Set<string>();

  Array.from(Array(count)).forEach(() => {
    let name;
    do {
      name = generateToggleName();
    } while (usedNames.has(name));

    usedNames.add(name);

    allFeatureToggles.push({
      name,
      description: faker.commerce.productDescription() || '',
      clientId: faker.helpers.arrayElement([
        'senet',
        'ops-center',
        'cedt',
        'vcp',
      ]),
      category:
        faker.helpers.arrayElement(['Release', 'Operation', 'Experiment']) ||
        '',
      isEnabled: faker.datatype.boolean(),
      ...assignAudience(currentUsers, currentOrgs),
      createdAt: faker.date.recent({ days: 60 }).toISOString(),
      createdBy: faker.person.fullName(),
      updatedAt: faker.date.recent({ days: 14 }).toISOString(),
      updatedBy: faker.person.fullName(),
    });
  });

  return allFeatureToggles;
};
